import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  useNodesState,
  useEdgesState,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Sun, Moon, Play, Loader2, CheckCircle2, Wrench, Clock3 } from 'lucide-react';

const STEP_MS = 750;

// Paleta por tipo de nodo del flujo (trigger / process / decision / external / human)
const KIND_STYLE = {
  trigger:  { dark: { bg: '#052e1f', border: '#00FF66', text: '#bff5d0' }, light: { bg: '#ecfdf5', border: '#059669', text: '#065f46' } },
  process:  { dark: { bg: '#1e1033', border: '#a56eff', text: '#e3d6ff' }, light: { bg: '#f5f3ff', border: '#7c3aed', text: '#4c1d95' } },
  decision: { dark: { bg: '#3a1f00', border: '#ff9b00', text: '#ffd9a0' }, light: { bg: '#fff7ed', border: '#ea580c', text: '#9a3412' } },
  external: { dark: { bg: '#001d33', border: '#04d9ff', text: '#b3f2ff' }, light: { bg: '#ecfeff', border: '#0891b2', text: '#155e63' } },
  human:    { dark: { bg: '#33001a', border: '#ff0080', text: '#ffb3d9' }, light: { bg: '#fdf2f8', border: '#db2777', text: '#831843' } },
};

// Estado de avance de un nodo (independiente del "kind"): live / pending / building
const STATUS_BADGE = {
  pending:  { icon: Clock3, label: 'Falta conectar', color: '#ff9b00' },
  building: { icon: Wrench, label: 'En construcción', color: '#04d9ff' },
};

// runIndex: posición del nodo/edge dentro de `path` (-1 si no participa en el recorrido)
function buildNodes(rawNodes, isDark, path, runStep, isRunning) {
  return rawNodes.map((n) => {
    const palette = KIND_STYLE[n.kind]?.[isDark ? 'dark' : 'light'] || KIND_STYLE.process[isDark ? 'dark' : 'light'];
    const order = path ? path.indexOf(n.id) : -1;
    const isActive = isRunning && order === runStep;
    const isDone = isRunning && order !== -1 && order < runStep;
    const badge = STATUS_BADGE[n.status];

    return {
      id: n.id,
      position: n.position,
      data: {
        label: (
          <div style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{n.label}</div>
              {n.sub && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{n.sub}</div>}
              {badge && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 10, fontWeight: 700, color: badge.color }}>
                  <badge.icon size={11} /> {badge.label}
                </div>
              )}
            </div>
            {isDone && <CheckCircle2 size={14} color={palette.border} />}
          </div>
        ),
      },
      style: {
        background: palette.bg,
        color: palette.text,
        border: `${isActive ? 2 : 1}px ${badge ? 'dashed' : 'solid'} ${badge ? badge.color : palette.border}`,
        borderRadius: 10,
        padding: '8px 12px',
        minWidth: 180,
        opacity: badge ? 0.85 : 1,
        transition: 'all .2s ease',
        boxShadow: isActive
          ? `0 0 0 4px ${palette.border}33, 0 0 24px ${palette.border}88`
          : isDark
          ? `0 0 16px ${palette.border}22`
          : '0 1px 4px rgba(0,0,0,0.08)',
      },
    };
  });
}

function buildEdges(rawEdges, isDark, path, runStep, isRunning) {
  const idleColor = isDark ? '#8b7bb8' : '#6d28d9';
  const doneColor = isDark ? '#00FF66' : '#059669';
  const activeColor = '#ff0080';

  return rawEdges.map((e) => {
    const targetOrder = path ? path.indexOf(e.target) : -1;
    const isActive = isRunning && targetOrder === runStep;
    const isDone = isRunning && targetOrder !== -1 && targetOrder < runStep;
    const badge = STATUS_BADGE[e.status];
    const color = badge ? badge.color : isActive ? activeColor : isDone ? doneColor : idleColor;

    return {
      ...e,
      animated: badge ? false : isActive || !isRunning,
      markerEnd: { type: MarkerType.ArrowClosed, color },
      style: { stroke: color, strokeWidth: isActive ? 2.5 : 1.5, strokeDasharray: badge ? '6 4' : undefined },
      labelStyle: { fill: badge ? badge.color : isDark ? '#e5e0ff' : '#3b0764', fontSize: 11, fontWeight: badge ? 700 : 400 },
      labelBgStyle: { fill: isDark ? '#1e1033' : '#f5f3ff' },
    };
  });
}

export default function ServiceFlowCanvas({ flow }) {
  const actions = flow.actions || [];
  const [activeKey, setActiveKey] = useState(
    (actions.find((a) => a.enabled) || actions[0])?.key
  );
  const activeAction = actions.find((a) => a.key === activeKey) || actions[0];

  const [isDark, setIsDark] = useState(true);
  const [runStep, setRunStep] = useState(-1);
  const [isRunning, setIsRunning] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const intervalRef = useRef(null);

  const path = useMemo(() => activeAction.path || [], [activeAction]);

  const styledNodes = useMemo(
    () => buildNodes(activeAction.nodes || [], isDark, path, runStep, isRunning),
    [activeAction, isDark, path, runStep, isRunning]
  );
  const styledEdges = useMemo(
    () => buildEdges(activeAction.edges || [], isDark, path, runStep, isRunning),
    [activeAction, isDark, path, runStep, isRunning]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(styledNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(styledEdges);

  useEffect(() => {
    setNodes(styledNodes);
    setEdges(styledEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styledNodes, styledEdges]);

  // Cambiar de acción detiene cualquier animación en curso
  useEffect(() => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setRunStep(-1);
    setJustFinished(false);
  }, [activeKey]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, markerEnd: { type: MarkerType.ArrowClosed } }, eds)),
    [setEdges]
  );

  const handleRun = () => {
    if (isRunning || !path.length) return;
    setJustFinished(false);
    setIsRunning(true);
    setRunStep(0);

    intervalRef.current = setInterval(() => {
      setRunStep((step) => {
        const next = step + 1;
        if (next >= path.length) {
          clearInterval(intervalRef.current);
          setTimeout(() => {
            setIsRunning(false);
            setRunStep(-1);
            setJustFinished(true);
            setTimeout(() => setJustFinished(false), 2500);
          }, STEP_MS);
          return step;
        }
        return next;
      });
    }, STEP_MS);
  };

  const chrome = isDark
    ? { bg: '#0f0a1a', panel: '#150f26', border: 'rgba(255,0,128,0.18)', text: '#f5f0ff', sub: 'rgba(245,240,255,0.55)' }
    : { bg: '#faf9fc', panel: '#ffffff', border: 'rgba(219,39,119,0.18)', text: '#1f1533', sub: 'rgba(31,21,51,0.55)' };

  return (
    <div
      style={{
        display: 'flex',
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${chrome.border}`,
        background: chrome.bg,
        boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.45)' : '0 12px 32px rgba(0,0,0,0.08)',
      }}
    >
      {/* Submenú tipo Workflow Builder con las acciones disponibles del servicio */}
      {actions.length > 1 && (
        <div
          style={{
            width: 220,
            flexShrink: 0,
            background: chrome.panel,
            borderRight: `1px solid ${chrome.border}`,
            padding: '14px 10px',
          }}
        >
          <div style={{ color: chrome.sub, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '0 6px 10px' }}>
            ACCIONES
          </div>
          {actions.map((action) => {
            const active = action.key === activeKey;
            return (
              <button
                key={action.key}
                type="button"
                disabled={!action.enabled}
                onClick={() => setActiveKey(action.key)}
                title={!action.enabled ? 'Próximamente' : action.description}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  marginBottom: 6,
                  borderRadius: 8,
                  border: active ? `1px solid ${action.dotColor}88` : '1px solid transparent',
                  background: active ? `${action.dotColor}1a` : 'transparent',
                  color: action.enabled ? chrome.text : chrome.sub,
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  cursor: action.enabled ? 'pointer' : 'not-allowed',
                  opacity: action.enabled ? 1 : 0.5,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: action.dotColor, flexShrink: 0 }} />
                {action.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Barra de título estilo ventana */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: chrome.panel,
            borderBottom: `1px solid ${chrome.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
            </div>
            <div>
              <div style={{ color: chrome.text, fontWeight: 700, fontSize: 13 }}>
                {flow.label} · {activeAction.label}
              </div>
              <div style={{ color: chrome.sub, fontSize: 11 }}>
                {justFinished ? '✅ Reporte generado (simulación)' : activeAction.description}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={handleRun}
              disabled={isRunning || !path.length}
              title="Simular el recorrido del flujo (representativo)"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid rgba(0,214,10,0.4)',
                background: 'rgba(0,214,10,0.12)',
                color: '#4dff5c',
                fontSize: 12,
                fontWeight: 600,
                cursor: isRunning || !path.length ? 'not-allowed' : 'pointer',
                opacity: !path.length ? 0.5 : 1,
              }}
            >
              {isRunning ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
              {isRunning ? 'Ejecutando…' : 'Run'}
            </button>
            <button
              type="button"
              onClick={() => setIsDark((v) => !v)}
              title={isDark ? 'Cambiar a vista clara' : 'Cambiar a vista oscura'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                borderRadius: 8,
                border: `1px solid ${chrome.border}`,
                background: 'transparent',
                color: '#ff0080',
                cursor: 'pointer',
              }}
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>
          </div>
        </div>

        {/* Lienzo del flujo */}
        <div style={{ height: 420 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background color={isDark ? '#2a2140' : '#e5d9ee'} gap={16} />
            <MiniMap
              zoomable
              pannable
              style={{ background: isDark ? '#1a1330' : '#f3edf7' }}
              maskColor={isDark ? 'rgba(15,10,26,0.7)' : 'rgba(255,255,255,0.7)'}
            />
            <Controls />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}
